import * as React from "react";
import { cn } from "./utils";
import { Elevated } from "./elevated";

function Card({
  className,
  interactive = false,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & {
  /**
   * Clickable card: applies the shared row/card click affordance (pointer +
   * hover background), matching interactive table rows (PRD §3). Pair with
   * `asChild` + a `<Link>` so the whole card is the click target.
   */
  interactive?: boolean;
  /** Render onto the child (e.g. a `<Link>`) instead of a wrapper div. */
  asChild?: boolean;
}) {
  return (
    <Elevated
      offset={1}
      shadowLevel={0}
      asChild={asChild}
      data-slot="card"
      className={cn(
        // The card treatment (docs/design/MINIMAL-SURFACES-PRD.md). Three things
        // separate a card from the page, and it takes all three — which is what
        // lets the dark canvas sit at near-black rgb(5,5,5):
        //   fill      `bg-card` — its OWN token, no longer the surface-2 rung
        //             the <Elevated> below paints. That class is emitted first
        //             and `bg-card` wins it through cn()/tailwind-merge; the
        //             <Elevated> stays for the SurfaceProvider context, so
        //             overlays opened inside a card still elevate correctly.
        //   hairline  full-strength `border-border` (an alpha hairline that
        //             re-reads itself against whatever it sits on), not the old
        //             `/60` fade of a fixed gray.
        //   shadow    `shadow-card` — a wide, soft pool, pulled back by a -50px
        //             spread so it sits UNDER the card instead of ringing it.
        // Nested cards used to differentiate by stepping up the fill ladder;
        // now that the fill is fixed, the hairline and the pool do that work.
        // `overflow-hidden` is load-bearing at this radius — 16px corners have
        // to clip their content or flush children square off the corner.
        // Spacing math (docs/design/SPACING-SYSTEM.md): the card frame and its
        // internal gap must NOT be the same number, or sections don't group.
        // Descending scale — 24px gutter/card-to-card (page rhythm) > 20px frame
        // (py-5, content↔edge) > 16px internal gap (gap-4, section↔section). The
        // horizontal gutter stays px-6 (CardHeader/Content/Footer own it).
        "text-foreground flex flex-col gap-[var(--card-gap)] overflow-hidden rounded-xl border border-border bg-card py-[var(--card-frame-py)] shadow-card",
        // Hover affordance: `bg-card-hover`, a dedicated token rather than a rung
        // of the ladder. The repo-wide rule for clickable surfaces/rows is
        // lighten or add a border on hover, never darken — but the light-mode
        // card is now pure white, so "one rung lighter" no longer exists there.
        // The token holds the INTENT (a perceptible step away from the resting
        // fill) and resolves per mode: lighter in dark, a faint wash in light.
        interactive &&
          "cursor-pointer transition-colors hover:bg-card-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-[var(--prose-gutter)] has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-[var(--card-frame-py)]",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("type-title", className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("type-subtext text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-[var(--prose-gutter)]", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center px-[var(--prose-gutter)] [.border-t]:pt-[var(--card-frame-py)]",
        className,
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardContent,
  CardFooter,
};
